import React, { useState, useEffect, Suspense, lazy, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Header } from './components/Header';
import { LoginForm } from './components/LoginForm';
import { Footer } from './components/Footer';
import { HomePage } from './components/HomePage';
import { signOut, auth } from './firebase';
import { useUser } from './contexts/UserContext';
import { AdminProvider } from './contexts/AdminContext';

// --- Code Splitting for Performance ---
const AdminLayout = lazy(() => import('./components/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const Dashboard = lazy(() => import('./components/admin/Dashboard').then(m => ({ default: m.Dashboard })));
const ProductsManager = lazy(() => import('./components/admin/ProductsManager').then(m => ({ default: m.ProductsManager })));
const ShopsManager = lazy(() => import('./components/admin/ShopsManager').then(m => ({ default: m.ShopsManager })));
const HomeSectionsManager = lazy(() => import('./components/admin/HomeSectionsManager').then(m => ({ default: m.HomeSectionsManager })));
const HistoryManager = lazy(() => import('./components/admin/HistoryManager').then(m => ({ default: m.HistoryManager })));
const UsersManager = lazy(() => import('./components/admin/UsersManager').then(m => ({ default: m.UsersManager })));
const OrdersManager = lazy(() => import('./components/admin/OrdersManager').then(m => ({ default: m.OrdersManager })));
const AdsManager = lazy(() => import('./components/admin/AdsManager').then(m => ({ default: m.AdsManager })));
const CategoriesManager = lazy(() => import('./components/admin/CategoriesManager').then(m => ({ default: m.CategoriesManager })));
const PagesBuilderManager = lazy(() => import('./components/admin/PagesBuilderManager').then(m => ({ default: m.PagesBuilderManager })));
const DebugPanel = lazy(() => import('./components/admin/DebugPanel').then(m => ({ default: m.DebugPanel })));
const SettingsManager = lazy(() => import('./components/admin/SettingsManager').then(m => ({ default: m.SettingsManager })));
const ShopsPage = lazy(() => import('./components/ShopsPage').then(m => ({ default: m.ShopsPage })));
const SearchPage = lazy(() => import('./components/SearchPage').then(m => ({ default: m.SearchPage })));
const CheckoutPage = lazy(() => import('./components/checkout/CheckoutPage').then(m => ({ default: m.CheckoutPage })));
const DynamicCustomPage = lazy(() => import('./components/DynamicCustomPage').then(m => ({ default: m.DynamicCustomPage })));
const ShopProfileInfo = lazy(() => import('./components/ShopProfileInfo').then(m => ({ default: m.ShopProfileInfo })));
const ProductDetailsPage = lazy(() => import('./components/ProductDetailsPage').then(m => ({ default: m.ProductDetailsPage })));
const AccountPage = lazy(() => import('./components/AccountPage').then(m => ({ default: m.AccountPage })));
import { GlobalHeader } from './components/GlobalHeader';
import { GlobalCart } from './components/GlobalCart';
import { PointsPage } from './components/PointsPage';
import { AboutPage } from './components/AboutPage';
import { ProfileModal } from './components/ProfileModal';
import { NotificationProvider, useNotification } from './components/Notification';
import { AssetPrefetcher } from './components/AssetPrefetcher';
import { MobileBottomNav } from './components/MobileBottomNav';
import { collection, query, where, onSnapshot, db } from './firebase';
// Wrapper to conditionally render Header/Footer based on route
const AppContent: React.FC<{ isDark: boolean; toggleTheme: () => void }> = ({ isDark, toggleTheme }) => {
  const { user, loading: userLoading } = useUser();
  const [view, setView] = useState<'login' | 'home'>('login');
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname.startsWith('/admin');

  console.log('AppContent render:', { user: !!user, loading: userLoading, pathname: location.pathname });

  // ALL HOOKS MUST BE BEFORE ANY CONDITIONAL RETURNS
  useEffect(() => {
    if (user) {
      setView('home');
    } else if (!userLoading) {
      setView('login');
    }
  }, [user, userLoading]);

  const { showNotification } = useNotification();
  const lastStatusesRef = useRef<Record<string, { status: string; paymentStatus: string }>>({});

  // 0. Listen for Order Status Changes (for Notifications)
  useEffect(() => {
    if (!user) {
      lastStatusesRef.current = {};
      return;
    }

    const q = query(collection(db, 'orders'), where('userId', '==', user.uid));
    const playNotificationSound = () => {
      const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
      audio.play().catch(e => console.warn('Sound play blocked:', e));
    };

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const docId = change.doc.id;
        const newData = change.doc.data();
        const currentStatus = newData.status;
        const currentPaymentStatus = newData.paymentStatus;

        if (change.type === 'modified') {
          const old = lastStatusesRef.current[docId];

          if (old) {
            let message = '';
            let type: 'info' | 'error' | 'success' | 'warning' = 'info';

            if (old.status !== currentStatus) {
              message = `تغيرت حالة طلبك #${docId.slice(0, 8)} إلى: ${getStatusText(currentStatus)}`;
              if (currentStatus === 'cancelled' && newData.cancelReason) {
                message += `\nالسبب: ${newData.cancelReason}`;
              }
              if (newData.isNewAccountOrder && currentStatus === 'cancelled') {
                message = "يرجى تأكيد جيميلك في أقرب وقت لتفعيل حسابك وإتمام الطلبات القادمة بنجاح.";
              }
              if (currentStatus === 'cancelled') type = 'error';
              if (currentStatus === 'delivered') type = 'success';
            } else if (old.paymentStatus !== currentPaymentStatus) {
              message = `تحديث دفع الطلب #${docId.slice(0, 8)}: ${getPaymentStatusText(currentPaymentStatus)}`;
              if (currentPaymentStatus === 'confirmed') type = 'success';
              if (currentPaymentStatus === 'failed') type = 'error';
            }

            if (message) {
              showNotification(message, type);
              playNotificationSound();

              // Native Browser Notification - Safety check for mobile WebViews
              if (typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'granted') {
                new window.Notification('Rashid Online', {
                  body: message,
                  icon: 'https://github.com/yousefkatato11-creator/main/blob/main/694eb8fe-2fdd-458e-aa8e-57969c337e81_20260204_031435_%D9%A0%D9%A0%D9%A0%D9%A0%20(1).png?raw=true'
                });
              }
            }
          }
        }

        // Always update the ref for any type of change (added, modified)
        lastStatusesRef.current[docId] = { status: currentStatus, paymentStatus: currentPaymentStatus };
      });
    });

    // Request notification permission - Safety check
    if (typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'default') {
      window.Notification.requestPermission();
    }

    return () => unsubscribe();
  }, [user, showNotification]);

  // Dynamic page titles
  useEffect(() => {
    const path = location.pathname;
    if (path === '/checkout') {
      document.title = `إتمام الشراء - Rashid Online`;
    } else if (path === '/search') {
      const query = new URLSearchParams(location.search).get('q');
      document.title = query ? `نتائج البحث: ${query}` : `البحث - Rashid Online`;
    } else if (path === '/shops') {
      document.title = `كل المحلات - Rashid Online`;
    } else if (path.startsWith('/admin')) {
      document.title = `لوحة التحكم - الادمن`;
    } else if (view === 'home' && user) {
      document.title = `الرئيسية - Rashid Online`;
    } else if (!user) {
      document.title = `Rashid Online`;
    }
  }, [location.pathname, location.search, view, user]);

  const handleLoginSuccess = () => {
    navigate('/');
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('currentPage');
      // Force navigate to home
      navigate('/');
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  // NOW we can do conditional returns AFTER all hooks
  if (userLoading) {
    return (
      <div className={`min-h-[100dvh] w-full flex items-center justify-center ${isDark ? 'bg-[#0f1115]' : 'bg-[#f8f9ff]'}`}>
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Admin Routes are handled separately
  if (isAdminRoute) {
    return (
      <AdminProvider>
        <Suspense fallback={<div className="min-h-[100dvh] w-full flex items-center justify-center"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div></div>}>
          <AdminLayout>
            <Routes>
              <Route path="/admin" element={<Dashboard />} />
              <Route path="/admin/products" element={<ProductsManager />} />
              <Route path="/admin/shops" element={<ShopsManager />} />
              <Route path="/admin/history" element={<HistoryManager />} />
              <Route path="/admin/sections" element={<HomeSectionsManager />} />
              <Route path="/admin/categories" element={<CategoriesManager />} />
              <Route path="/admin/pages-builder" element={<PagesBuilderManager />} />
              <Route path="/admin/users" element={<UsersManager />} />
              <Route path="/admin/orders" element={<OrdersManager />} />
              <Route path="/admin/ads" element={<AdsManager />} />
              <Route path="/admin/debug" element={<DebugPanel />} />
              <Route path="/admin/settings" element={<SettingsManager />} />
            </Routes>
          </AdminLayout>
        </Suspense>
      </AdminProvider>
    );
  }

  if (location.pathname === '/checkout') {
    return (
      <div className="w-full h-full page-transition">
        <Suspense fallback={<div className="min-h-[100dvh] w-full flex items-center justify-center"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div></div>}>
          <CheckoutPage isDark={isDark} />
        </Suspense>
      </div>
    );
  }

  // Common Layout for Home, Shops, Search, etc.
  return (
    <div className={`w-full min-h-screen max-w-[100vw] overflow-x-hidden pb-24 md:pb-0 ${isDark ? 'bg-[#0f1115]' : 'bg-[#f8f9ff]'}`}>
      <GlobalHeader isDark={isDark} toggleTheme={toggleTheme} onLogout={handleLogout} />

      <Suspense fallback={<div className="min-h-[100dvh] w-full flex items-center justify-center"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div></div>}>
        <Routes>
          <Route path="/login" element={
            <div
              className={`h-[100dvh] w-full relative flex flex-col items-center justify-between text-white bg-cover bg-center bg-no-repeat transition-all duration-700 select-none overflow-hidden`}
              style={{
                backgroundImage: `linear-gradient(rgba(0,0,0,${isDark ? '0.75' : '0.3'}), rgba(0,0,0,${isDark ? '0.75' : '0.3'})), url('https://raw.githubusercontent.com/yousefkatato11-creator/main/main/main.jpg')`,
              }}
            >
              <div className={`absolute inset-0 ${isDark ? 'bg-black/40' : 'bg-black/5'} pointer-events-none transition-colors duration-700`}></div>
              <div className="relative z-10 w-full">
                <Header isDark={isDark} toggleTheme={toggleTheme} />
              </div>
              <main className="relative z-10 w-full max-w-md px-4 py-2 md:py-6 flex flex-col items-center justify-center flex-1 space-y-3 md:space-y-6 pb-10 overflow-hidden">
                <div className="w-full text-center shrink-0 flex flex-col items-center justify-center pt-4">
                  <img src="/logo-login.png" alt="Logo" className="h-16 md:h-28 w-auto object-contain drop-shadow-2xl mb-1 md:mb-2" />
                  <h2 className={`w-full text-lg md:text-2xl font-black mb-1 text-center`} style={{ color: '#ffffff', textShadow: '0 0 12px rgba(0,0,0,0.9)' }}><span>جبنالك رشيد كلها لحد عندك</span></h2>
                  <p className="w-full text-xs md:text-lg font-bold text-white drop-shadow-md text-center shrink-0">لف السوق كله ... بتليفونك</p>
                </div>
                <div className="w-full pb-8">
                  <LoginForm isDark={isDark} onLoginSuccess={handleLoginSuccess} />
                </div>
              </main>
              <div className="relative z-10 w-full">
                <Footer isDark={isDark} isLoginView={true} />
              </div>
            </div>
          } />
          <Route path="/shops" element={
            <div className={`w-full min-h-screen page-transition ${isDark ? 'bg-[#0f1115] text-white' : 'bg-[#f8f9ff] text-slate-900'}`}>
              <ShopsPage isDark={isDark} />
              <Footer isDark={isDark} />
            </div>
          } />
          <Route path="/category/:id" element={
            <div className={`w-full min-h-screen page-transition ${isDark ? 'bg-[#0f1115] text-white' : 'bg-[#f8f9ff] text-slate-900'}`}>
              <HomePage isDark={isDark} toggleTheme={toggleTheme} onLogout={handleLogout} user={user} />
              <Footer isDark={isDark} />
            </div>
          } />
          <Route path="/search" element={
            <div className={`w-full min-h-screen page-transition ${isDark ? 'bg-[#0f1115] text-white' : 'bg-[#f8f9ff] text-slate-900'}`}>
              <SearchPage isDark={isDark} />
              <Footer isDark={isDark} />
            </div>
          } />
          <Route path="/account" element={
            <div className={`w-full min-h-screen page-transition ${isDark ? 'bg-[#0f1115] text-white' : 'bg-[#f8f9ff] text-slate-900'}`}>
              <AccountPage isDark={isDark} />
            </div>
          } />
          <Route path="/cart" element={
            <div className={`w-full min-h-screen page-transition ${isDark ? 'bg-[#0f1115] text-white' : 'bg-[#f8f9ff] text-slate-900'}`}>
              <HomePage isDark={isDark} toggleTheme={toggleTheme} onLogout={handleLogout} user={user} />
            </div>
          } />
          <Route path="/points" element={
            <div className={`w-full min-h-screen page-transition ${isDark ? 'bg-[#0f1115] text-white' : 'bg-[#f8f9ff] text-slate-900'}`}>
              <PointsPage isDark={isDark} />
              <Footer isDark={isDark} />
            </div>
          } />
          <Route path="/about" element={
            <div className={`w-full min-h-screen page-transition ${isDark ? 'bg-[#0f1115] text-white' : 'bg-[#f8f9ff] text-slate-900'}`}>
              <AboutPage isDark={isDark} />
              <Footer isDark={isDark} />
            </div>
          } />
          <Route path="/page/:pageId" element={
            <div className={`w-full min-h-screen page-transition ${isDark ? 'bg-[#0f1115] text-white' : 'bg-[#f8f9ff] text-slate-900'}`}>
              <DynamicCustomPage isDark={isDark} toggleDark={toggleTheme} />
            </div>
          } />
          <Route path="/shop/:id" element={
            <div className={`w-full min-h-screen page-transition ${isDark ? 'bg-[#0f1115] text-white' : 'bg-[#f8f9ff] text-slate-900'}`}>
              <ShopProfileInfo isDark={isDark} />
            </div>
          } />
          <Route path="/product/:id" element={
            <ProductDetailsPage isDark={isDark} />
          } />
          <Route path="/*" element={
            <div className="w-full min-h-screen page-transition">
              <HomePage isDark={isDark} toggleTheme={toggleTheme} onLogout={handleLogout} user={user} />
            </div>
          } />
        </Routes>
      </Suspense>

      <GlobalCart isDark={isDark} />
      <MobileBottomNav isDark={isDark} onLogout={handleLogout} />
      <SpeedInsights />
    </div >
  );
};

const getStatusText = (status: string) => {
  switch (status) {
    case 'pending': return 'قيد الانتظار';
    case 'processing': return 'جاري التجهيز';
    case 'shipped': return 'تم الشحن';
    case 'delivered': return 'تم التوصيل 🎉';
    case 'cancelled': return 'ملغي ❌';
    default: return status;
  }
};

const getPaymentStatusText = (status: string) => {
  switch (status) {
    case 'pending_confirmation': return 'في انتظار التأكيد';
    case 'under_review': return 'قيد المراجعة';
    case 'confirmed': return 'تم التأكيد ✅';
    case 'waiting_payment': return 'في انتظار الدفع';
    case 'failed': return 'فشل الدفع ❌';
    case 'refunded': return 'تم الارجاع';
    default: return status;
  }
};

const App: React.FC = () => {
  const [isDark, setIsDark] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });

  useEffect(() => {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    if (isDark) {
      document.body.style.backgroundColor = '#0f1115';
    } else {
      document.body.style.backgroundColor = '#f8f9ff';
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);

  return (
    <BrowserRouter>
      <NotificationProvider>
        <AssetPrefetcher />
        <AppContent isDark={isDark} toggleTheme={toggleTheme} />
      </NotificationProvider>
    </BrowserRouter>
  );
};

export default App;
